import { UzButton, useBranding } from '@utazzo/ui';
import { ArrowLeft, Printer } from 'lucide-react';
import React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { amountToWords, formatDateDMY, formatInrPaise } from '../invoices.helpers';
import { useInvoice, useInvoiceLedger } from '../use-invoices';

import type { InvoiceDetail } from '@utazzo/shared';

/**
 * Full-page customer payment receipt (route /invoices/:id/receipt/print).
 * Mirrors the tax-invoice / credit-note print sheets: a single bordered outer
 * table with thin black borders, compact cells and print-oriented styling. The
 * `@media print` block scopes the printed output to `#receipt-sheet`, hiding
 * the app chrome so the receipt prints (or saves to PDF) on its own.
 *
 * The receipt acknowledges every non-voided payment recorded against the
 * invoice, itemised by date + mode, with the running paid total in figures and
 * words - the "Receipt Format - Haryana" layout. Company header, CIN, GSTIN and
 * terms come from the billing-entity settings (global config).
 */

const SHEET_CSS = `
#receipt-sheet {
  color: #000;
  background: #fff;
  font-family: Calibri, Candara, Segoe, "Segoe UI", Arial, sans-serif;
  font-size: 13px;
  line-height: 1.35;
}
#receipt-sheet .rcp-title {
  text-align: center;
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
  letter-spacing: 0.05em;
}
#receipt-sheet .rcp-logo {
  width: 200px;
  max-width: 55%;
  height: auto;
  margin: 4px auto 6px;
  display: block;
}
#receipt-sheet table.rcp {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
}
#receipt-sheet table.rcp > tbody > tr > td {
  border: 1px solid #000;
  padding: 3px 6px;
  vertical-align: top;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}
#receipt-sheet .rcp-c { text-align: center; }
#receipt-sheet .rcp-r { text-align: right; }
#receipt-sheet .rcp-b { font-weight: 700; }
#receipt-sheet .rcp-note { font-size: 10px; }

@media print {
  body * { visibility: hidden; }
  #receipt-sheet, #receipt-sheet * { visibility: visible; }
  #receipt-sheet {
    position: absolute;
    inset: 0;
    margin: 0;
    padding: 24px;
    box-shadow: none !important;
    border: none !important;
    max-width: none !important;
    width: 100%;
  }
  .receipt-no-print { display: none !important; }
  @page { margin: 12mm; }
}
`;

/** "For <destination> Package" / product summary shown below the total. */
const packageLabel = (inv: InvoiceDetail): string => {
  if (inv.destination) return `For ${inv.destination} Package`;
  const products = inv.products.map((p) => p.name).filter(Boolean);
  if (products.length) return `For ${products.join(', ')}`;
  if (inv.hotelName) return `For ${inv.hotelName}`;
  return 'For travel services';
};

export const ReceiptPrintPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // The specific payment this receipt acknowledges (`?r=<receiptId>`); when
  // absent, the latest payment - so the header button prints the fullest receipt.
  const targetReceiptId = searchParams.get('r');
  const invoiceQuery = useInvoice(id);
  const ledgerQuery = useInvoiceLedger(id);
  const branding = useBranding();

  if (invoiceQuery.isLoading || ledgerQuery.isLoading) {
    return <div className="p-8 text-sm text-zinc-500">Loading receipt...</div>;
  }
  if (invoiceQuery.isError || !invoiceQuery.data) {
    return (
      <div className="space-y-4 p-8">
        <p className="text-sm text-red-600">Could not load this invoice.</p>
        <Link to="/invoices" className="text-primary text-sm underline">
          Back to invoices
        </Link>
      </div>
    );
  }

  const inv = invoiceQuery.data;
  const entity = inv.billingEntity;
  const ledger = ledgerQuery.data;

  // Current (non-voided) receipts, oldest first. The receipt document is
  // cumulative: it lists every payment up to and including the target one, with
  // the running total received so far (mirrors the sample, where one numbered
  // receipt shows both payments and their combined total).
  const allReceipts = (ledger?.receipts ?? [])
    .filter((r) => !r.voidedAt)
    .sort(
      (a, b) =>
        a.receiptDate.localeCompare(b.receiptDate) || a.createdAt.localeCompare(b.createdAt),
    );
  const requestedIndex = targetReceiptId
    ? allReceipts.findIndex((r) => r.id === targetReceiptId)
    : allReceipts.length - 1;
  const targetIndex = requestedIndex >= 0 ? requestedIndex : allReceipts.length - 1;
  const target = allReceipts[targetIndex] ?? null;

  const receipts = target ? allReceipts.slice(0, targetIndex + 1) : allReceipts;
  const totalPaidPaise = String(receipts.reduce((sum, r) => sum + Number(r.amountPaise), 0));

  const customerName = inv.buyer?.name ?? inv.customerName ?? '-';
  // The minted UTZR number for this payment; legacy receipts have none, so fall
  // back to the proforma/invoice number as a stable reference.
  const receiptNo =
    target?.receiptNumber ?? inv.proformaNumber ?? inv.taxInvoiceNumber ?? inv.invoiceNumber;
  const receiptDate = formatDateDMY(target?.receiptDate ?? new Date().toISOString());

  const entityCityState = [entity.city, entity.stateName].filter(Boolean).join(', ');
  const addressLines = [entity.addressLine1, entity.addressLine2, entityCityState].filter(Boolean);
  const logoUrl = entity.logoUrl ?? branding.logoUrl ?? null;

  return (
    <div className="space-y-4">
      <style>{SHEET_CSS}</style>

      {/* Toolbar (never printed). */}
      <div className="receipt-no-print flex items-center justify-between">
        <Link
          to={`/invoices/${inv.id}`}
          className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to invoice
        </Link>
        <UzButton leftIcon={<Printer className="h-4 w-4" />} onClick={() => window.print()}>
          Print / Save as PDF
        </UzButton>
      </div>

      {/* The printable sheet. */}
      <div
        id="receipt-sheet"
        className="mx-auto max-w-3xl border border-zinc-200 bg-white p-8 shadow-sm"
      >
        <table className="rcp">
          <colgroup>
            <col style={{ width: '18%' }} />
            <col style={{ width: '52%' }} />
            <col style={{ width: '30%' }} />
          </colgroup>
          <tbody>
            {/* Masthead: logo + legal name + address (left) and receipt no (right). */}
            <tr>
              <td colSpan={2} className="rcp-c">
                {logoUrl && <img className="rcp-logo" src={logoUrl} alt={entity.legalName} />}
                <div className="rcp-b">{entity.legalName}</div>
                {addressLines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
                {entity.cin && (
                  <div>
                    <span className="rcp-b">CIN:</span> {entity.cin}
                  </div>
                )}
                <div>
                  <span className="rcp-b">GST No.:</span> {entity.gstin}
                </div>
              </td>
              <td>
                <div>
                  <span className="rcp-b">Receipt No.:</span>
                  <div>{receiptNo}</div>
                </div>
                <div style={{ marginTop: 6 }}>
                  <span className="rcp-b">Receipt Date:</span>
                  <div>{receiptDate}</div>
                </div>
                {entity.phone && (
                  <div style={{ marginTop: 6 }}>
                    <span className="rcp-b">Contact:</span> {entity.phone}
                  </div>
                )}
                {entity.email && (
                  <div>
                    <span className="rcp-b">Email:</span> {entity.email}
                  </div>
                )}
              </td>
            </tr>

            {/* Title. */}
            <tr>
              <td colSpan={3} className="rcp-c">
                <div className="rcp-title">PAYMENT RECEIPT</div>
              </td>
            </tr>

            {/* Received from / amount. */}
            <tr>
              <td colSpan={2}>
                <span className="rcp-b">Received from:</span> {customerName}
              </td>
              <td>
                <span className="rcp-b">Received Amount:</span>
                <div>{formatInrPaise(totalPaidPaise)}</div>
              </td>
            </tr>
            <tr>
              <td colSpan={3}>
                <span className="rcp-b">Amount in words:</span> {amountToWords(totalPaidPaise)}
              </td>
            </tr>

            {/* Particulars header. */}
            <tr>
              <td className="rcp-b rcp-c">Date</td>
              <td className="rcp-b rcp-c">Particular</td>
              <td className="rcp-b rcp-c">Amount</td>
            </tr>

            {/* Payment lines. */}
            {receipts.length === 0 ? (
              <tr>
                <td colSpan={3} className="rcp-c">
                  No payments recorded.
                </td>
              </tr>
            ) : (
              receipts.map((r) => (
                <tr key={r.id}>
                  <td className="rcp-c">{formatDateDMY(r.receiptDate)}</td>
                  <td>
                    {r.paymentMode ?? 'Payment'}
                    {r.referenceNo ? ` (${r.referenceNo})` : ''}
                  </td>
                  <td className="rcp-r">{formatInrPaise(r.amountPaise)}</td>
                </tr>
              ))
            )}

            {/* Total. */}
            <tr>
              <td colSpan={2} className="rcp-b rcp-r">
                Total
              </td>
              <td className="rcp-b rcp-r">{formatInrPaise(totalPaidPaise)}</td>
            </tr>

            {/* Package / booking line. */}
            <tr>
              <td colSpan={3} className="rcp-b">
                {packageLabel(inv)}
              </td>
            </tr>

            {/* Footer terms. */}
            <tr>
              <td colSpan={3} className="rcp-note">
                <div>Kindly note: All disputes are subject to Delhi Jurisdiction.</div>
                <div>
                  1. All payments by Cheque / DD should be in favour of &lsquo;
                  {entity.legalName}&rsquo;.
                </div>
                <div>2. All cheque payments are subject to realization.</div>
                <div>
                  3. Any fake currency note received would be impounded by the bank and will not be
                  returned; the customer would also be liable to pay the value of the impounded
                  currency.
                </div>
                <div style={{ marginTop: 4, textAlign: 'center' }}>
                  *This is a system-generated document. No physical signature is required.*
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
